import { makeAutoObservable } from 'mobx';
import type RootStore from '../RootStore';

export class ClipboardStore {
    rootStore: RootStore;
    isSupported = 'clipboard' in navigator;

    constructor(rootStore: RootStore) {
        makeAutoObservable(this, {
            rootStore: false,
        });
        this.rootStore = rootStore;
    }

    async writeText(text: string): Promise<void> {
        if (!this.isSupported) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                document.execCommand('copy');
            } finally {
                document.body.removeChild(textArea);
            }
            return;
        }

        await navigator.clipboard.writeText(text);

        if (this.rootStore.notificationStore) {
            this.rootStore.notificationStore.addNotification({
                message: 'Copied to clipboard',
                options: {
                    variant: 'success',
                    autoHideduration: 2000,
                },
            });
        }
    }

    async readText(): Promise<string> {
        if (!this.isSupported) {
            throw new Error('Clipboard API not supported');
        }
        return await navigator.clipboard.readText();
    }

    async writeData(items: ClipboardItem[]): Promise<void> {
        if (!this.isSupported) {
            throw new Error('Clipboard API not supported');
        }
        await navigator.clipboard.write(items);
    }

    async readData(): Promise<ClipboardItem[]> {
        if (!this.isSupported) {
            throw new Error('Clipboard API not supported');
        }
        return await navigator.clipboard.read();
    }
}